import * as React from 'react';
import { ReactNode } from 'react';
import './Tab.scss';

interface ITabProps {
  isActive?: boolean;
  headerText: string;
  className?: string;
  tabBarClassName?: string;
  disable?: boolean;
  children: ReactNode;
}

const Tab: React.FunctionComponent<ITabProps> = ({
  isActive = false,
  children,
  headerText,
  className,
  tabBarClassName,
  disable,
}) => {
  return (
    <div
      className={`${className || ''} gcl-gems-tabs-tab ${
        isActive ? 'gcl-gems-tabs-tab-active' : 'gcl-gems-tabs-tab-inactive'
      }`}
    >
      {children}
    </div>
  );
};

interface ITabsProps {
  className?: string;
  activeTabIndex?: number;
  onActiveTabIndexChanged?: (index: number) => void;
  children: (false | React.ReactElement<ITabProps>)[];
}

interface ITabsComposition {
  Tab?: typeof Tab;
}

const Tabs: React.FunctionComponent<ITabsProps> & ITabsComposition = ({
  children,
  className,
  activeTabIndex = 0,
  onActiveTabIndexChanged = (index) => {},
}) => {
  const [isActiveIndex, setIsActiveIndex] = React.useState(0);

  React.useEffect(() => {
    setIsActiveIndex(activeTabIndex || 0);
  }, [activeTabIndex]);

  const onClickTab = (index: number) => {
    setIsActiveIndex(index);
    onActiveTabIndexChanged(index);
  };

  const childrenTabs: ReactNode[] = [];
  const tabBar = React.Children.map(children as any, (child: React.ReactElement<ITabProps>, index: number) => {
    if (!React.isValidElement<ITabProps>(child)) {
      return child;
    }
    const isActive = isActiveIndex === index;
    childrenTabs.push(React.cloneElement(child, { isActive, key: child.props.headerText }));
    return (
      <div
        onClick={() => {
          if (child.props.disable) return;
          onClickTab(index);
        }}
        className={`${className || ''} ${
          isActiveIndex === index ? 'gcl-gems-tabs-tab-bar-item-active' : ''
        } gcl-gems-tabs-tab-bar-item ${child.props.tabBarClassName || ''} ${
          child.props.disable ? 'gcl-gems-tabs-tab-bar-item-disable' : ''
        }`}
        key={`${child.props.headerText}_tab_bar`}
      >
        {child.props.headerText}
      </div>
    );
  });

  return (
    <div className={`${className || ''}`}>
      <div className="gcl-gems-tabs-tab-bar">{tabBar}</div>
      {childrenTabs}
    </div>
  );
};

Tabs.Tab = Tab;

export { Tabs, Tab };
